"use client";

import React from "react";
import { MapPin, Phone, Mail, Facebook, Twitter, Instagram, ShieldCheck, Leaf, House } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-24 animate-pulse rounded-md bg-primary-foreground/20" />)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <NavigationMenu viewport={false} className="max-w-full justify-start">
      <NavigationMenuList className="flex-col items-start gap-2 space-x-0">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i} className="w-full">
            <NavigationMenuLink asChild className="w-full justify-start bg-transparent px-0 py-1 text-secondary transition-colors hover:bg-transparent hover:text-secondary/80 focus:bg-transparent data-[active=true]:bg-transparent">
              <Link to={item.href || "#"}>{item.label}</Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function FooterInfo() {
  const businessDescription = "JUNKR is Ottawa's premier locally owned junk removal service. We provide fast, eco-friendly disposal solutions for residential and commercial properties across Kanata, Nepean, and the greater Ottawa area.";
  const address = "Ottawa, Ontario, Canada";
  const phone = "613-555-0123";
  const email = "samburland@gmail.com";
  const serviceAreas = ["Ottawa", "Kanata", "Orleans", "Barrhaven", "Nepean", "Gloucester", "Stittsville", "Carp", "Rockland"];
  const staticLinks = [{
    label: "Services",
    href: "/ServicesPage"
  }, {
    label: "Pricing",
    href: "/PricingPage"
  }, {
    label: "About",
    href: "/AboutPage"
  }, {
    label: "Blog",
    href: "/BlogPage"
  }];
  const socials = [{
    name: "Facebook",
    icon: Facebook,
    href: "#"
  }, {
    name: "Twitter",
    icon: Twitter,
    href: "#"
  }, {
    name: "Instagram",
    icon: Instagram,
    href: "#"
  }];
  const trustBadges = [{
    label: "Locally Owned",
    icon: House
  }, {
    label: "Fully Insured",
    icon: ShieldCheck
  }, {
    label: "Eco-Friendly",
    icon: Leaf
  }];
  return <section data-section-id="259801" id="footer" className="dark bg-background py-16 text-foreground">
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-8">
          {}
          <div className="md:col-span-5 lg:col-span-4">
            <Link to="/" className="inline-block">
              <WvcLogo className="h-8 w-auto text-primary" />
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-foreground/70 font-default">
              {businessDescription}
            </p>
            
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-foreground/50">Service Areas:</span>
              <p className="text-xs text-foreground/60 leading-relaxed">
                {serviceAreas.join(", ")}
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              {trustBadges.map((badge, i) => {
              const Icon = badge.icon;
              return <div key={i} data-index={i} className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary border border-primary/20">
                    <Icon className="size-3" />
                    {badge.label}
                  </div>;
            })}
            </div>
          </div>

          {}
          <div className="md:col-span-2 lg:col-span-2">
            <h3 className="font-default mb-6 text-lg font-bold tracking-tight text-primary">
              Company
            </h3>
            <MenuProvider menu_id="45">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="md:col-span-2 lg:col-span-2">
            <h3 className="font-default mb-6 text-lg font-bold tracking-tight text-primary">
              Links
            </h3>
            <ul className="flex flex-col gap-2">
              {staticLinks.map((link, i) => <li key={i} data-index={i}>
                  <Link to={link.href} className="text-sm text-secondary transition-colors hover:text-secondary/80">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="md:col-span-3 lg:col-span-4 lg:justify-self-end">
            <h3 className="font-default mb-6 text-lg font-bold tracking-tight text-primary">
              Contact Us
            </h3>
            <ul className="flex flex-col gap-4 text-sm text-foreground/70 font-default">
              {address && <li className="flex items-start gap-3">
                  <MapPin className="mt-0.5 size-5 shrink-0 text-primary" />
                  <span>{address}</span>
                </li>}
              {phone && <li className="flex items-center gap-3">
                  <Phone className="size-5 shrink-0 text-primary" />
                  <Link to={`tel:${phone}`} className="text-secondary hover:text-secondary/80 transition-colors">
                    Phone/Text: {phone}
                  </Link>
                </li>}
              {email && <li className="flex items-center gap-3">
                  <Mail className="size-5 shrink-0 text-primary" />
                  <Link to={`mailto:${email}`} className="text-secondary hover:text-secondary/80 transition-colors">
                    {email}
                  </Link>
                </li>}
            </ul>

            <div className="mt-8 flex items-center gap-4">
              {socials.map((social, i) => {
              const Icon = social.icon;
              return <Link key={social.name} data-index={i} to={social.href} className="rounded-lg bg-secondary/20 p-2 text-foreground transition-all hover:bg-primary hover:text-primary-foreground shadow-md" aria-label={social.name}>
                    <Icon className="size-5" />
                  </Link>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 border-t border-border pt-8 text-center text-sm text-foreground/40 font-mono">
          <p>
            &copy; {new Date().getFullYear()} JUNKR Ottawa. Serving Greater Ottawa since 2024. All rights reserved. | Made in Ottawa
          </p>
        </div>
      </div>
    </section>;
}